import { inject, Injectable } from '@angular/core';
import { orderBy, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { MatchRequest } from '@models';
import { DeepPartial } from '@models';
import { getCallableErrorCode } from '@utils';
import { combineLatest, map, of, shareReplay, switchMap } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { TenantContextService } from './tenant-context.service';
import { UserService } from './current-user.service';
import { TeamsService } from './team.service';

@Injectable({
  providedIn: 'root',
})
export class MatchRequestService {
  private readonly firestore = inject(FirestoreService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly seasonManager = inject(SeasonManagerService);
  private readonly userService = inject(UserService);
  private readonly teamsService = inject(TeamsService);
  private readonly functions = inject(Functions);

  private get col() {
    return this.tenantContext.collections.REQUESTS;
  }

  public all$ = this.seasonManager.season$.pipe(
    switchMap((season) =>
      this.firestore.listDocuments<MatchRequest>(
        this.col,
        where('seasonYear', '==', season),
        orderBy('updatedAt', 'desc'),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public myRequests$ = this.userService.user$.pipe(
    switchMap((user) =>
      this.teamsService.all$.pipe(
        map((teams) => teams.filter((t) => t.delegatesIds?.includes(user.id))),
      ),
    ),
    switchMap((teams) => {
      if (!teams || teams.length === 0) return of([]);
      const teamsIds = teams.map((t) => t.id);
      const chunks: string[][] = [];
      for (let i = 0; i < teamsIds.length; i += 30) {
        chunks.push(teamsIds.slice(i, i + 30));
      }
      return combineLatest(
        chunks.map((chunk) =>
          this.firestore.listDocuments<MatchRequest>(
            this.col,
            where('toId', 'in', chunk),
            where('status', '==', 'PENDING'),
          ),
        ),
      ).pipe(map((results) => results.flat()));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  get(id: string) {
    return this.firestore.getDocument<MatchRequest>(this.col, id);
  }

  getByMatch(matchId: string, teamId: string, to: 'toId' | 'fromId' = 'toId') {
    return this.firestore
      .listDocuments<MatchRequest>(
        this.col,
        where('matchId', '==', matchId),
        where('status', '==', 'PENDING'),
        where(to, '==', teamId),
      )
      .pipe(map((requests) => requests[0]));
  }

  getAnyRequestByMatch(matchId: string) {
    return this.firestore
      .listDocuments<MatchRequest>(this.col, where('matchId', '==', matchId))
      .pipe(map((requests) => requests[0]));
  }

  create(request: Omit<MatchRequest, 'id'>) {
    return this.firestore
      .create<MatchRequest>(this.col, request)
      .then(async (id) => {
        try {
          const fn = httpsCallable<{ requestId: string }, { success: boolean }>(
            this.functions,
            'notifyMatchRequest',
          );
          await fn({ requestId: id });
        } catch (error) {
          // El servidor valida la fecha propuesta (regla de @sport-core
          // change-date-rule): si es inválida borra la solicitud y responde
          // invalid-argument con el motivo en castellano. Ese rechazo se
          // propaga para que la UI lo muestre; cualquier otro fallo de la
          // notificación no bloquea la creación de la solicitud.
          if (getCallableErrorCode(error) === 'invalid-argument') {
            throw error;
          }
        }
        return id;
      });
  }

  update(id: string, partial: DeepPartial<MatchRequest>) {
    return this.firestore.update<MatchRequest>(this.col, {
      id,
      ...partial,
    } as Partial<MatchRequest>);
  }

  createOrUpdate(id: string | undefined, request: Omit<MatchRequest, 'id'>) {
    return id ? this.update(id, request) : this.create(request);
  }

  accept(requestId: string) {
    const callable = httpsCallable(this.functions, 'answerMatchRequest');
    return callable({ requestId, answer: 'ACCEPTED' });
  }

  reject(requestId: string) {
    const callable = httpsCallable(this.functions, 'answerMatchRequest');
    return callable({ requestId, answer: 'REJECTED' });
  }
}
